/** Pure-literal flexbox layout presets for inline style objects -- the display/direction/alignment idiom runs that
 * recurred hundreds of times across the frontend trees (measured 2026-08-06: 426 inline occurrences over apps/,
 * components/, mfe/, spa/). Consumers spread a preset and add the per-site members: `{ ...FLEX_COLUMN_STYLE, gap:
 * spacing[2] }`. Sibling of `const/style-size-presets.ts`, the same pure-literal preset pattern. FLEX_CENTER_STYLE
 * belongs here with the rest, so this leaf is the ONE home of the idiom set --
 * `QUAL-STYLE-FLEX-IDIOM-INLINE-001` derives every arm's runs, key sets and prefilter from the preset records at module
 * load (`.claude/skills/compliance-rule-authoring/SKILL.md` `## Runtime-derived SSOT-bypass rules (authoring pattern)`).
 *
 * WHAT IS DERIVED -- since 2026-09-24 the matcher reads this leaf WHOLE (`PRESET_LEAVES`), so EVERY export here is a
 * governed preset: add only presets to this leaf. The run text, the member key set, the family guard and the prefilter
 * all follow this leaf's values, so adding or editing a preset needs no matcher edit. A preset member of a new VALUE
 * SHAPE still does (the NUMERIC members of {@link FLEX_FILL_TRUNCATE_STYLE} and {@link FLEX_FILL_SCROLL_Y_STYLE} did).
 *
 * THE ROW PRESETS DECLARE THEIR ROW (iss-b4d63a93, iss-55e987ee). A preset that omits `flexDirection` cannot override
 * one, so a ROW preset spread onto a container whose base opens a column (`Card` opens with {@link FLEX_COLUMN_STYLE})
 * rendered a column. `flexDirection: 'row'` is also the CSS initial value, so the matcher reads it as an IMPLIED member:
 * a hand spelling with or without it still re-spells the row preset.
 *
 * TWO FAMILIES IN ONE LEAF. The ten idiom presets lay out an element's CHILDREN; the two FILL
 * presets size the element itself INSIDE ITS OWN PARENT. They compose rather than conflict -- a scroll pane is
 * `{ ...FLEX_COLUMN_STYLE, ...FLEX_FILL_SCROLL_Y_STYLE }` -- so the matcher reads them as separate COMPOSITION
 * families under one owning leaf, and its family guard never declines a container preset for a `flex`/`minWidth`/
 * `overflowY` member sitting beside it.
 */

/** Flex row with both axes centered: the icon-button / placeholder / overlay base. */
export const FLEX_CENTER_STYLE = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
} as const;

/** Flex column base -- the `display`/`flexDirection` pair every stacked layout opens with. */
export const FLEX_COLUMN_STYLE = {
	display: 'flex',
	flexDirection: 'column',
} as const;

/** Flex row with vertically centered items -- the row-layout base (no justify: the per-site object decides main-axis
 * distribution, or spreads FLEX_BETWEEN_STYLE / FLEX_CENTER_STYLE instead). */
export const FLEX_ROW_CENTER_STYLE = {
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
} as const;

/** Inline-flex row with vertically centered items -- the badge/pill/inline-control base. */
export const INLINE_FLEX_ROW_CENTER_STYLE = {
	display: 'inline-flex',
	flexDirection: 'row',
	alignItems: 'center',
} as const;

/** Flex row, items centered, ends pushed apart -- the header/toolbar/meta-row base. */
export const FLEX_BETWEEN_STYLE = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
} as const;

/** Flex row whose items hang from the TOP rather than centering -- the banner/notice/avatar-row base, where the
 *  leading icon or avatar must sit level with the FIRST line of a block of text that may wrap to several. The
 *  cross-axis sibling of {@link FLEX_ROW_CENTER_STYLE}; it was spelled longhand at ELEVEN sites across apps/,
 *  components/, mfe/ and spa/ under nine local names before it was one decision. */
export const FLEX_ROW_START_STYLE = {
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'flex-start',
} as const;

/** Flex row with ends pushed apart and items hanging from the TOP -- the card/panel/modal HEADER base, where a
 *  multi-line title on the left must stay level with a single-line action or badge cluster on the right. The
 *  cross-axis sibling of {@link FLEX_BETWEEN_STYLE}: same main-axis decision, and the top alignment is what keeps the
 *  right-hand group from drifting to the vertical middle of a title that wraps. It was spelled longhand at THIRTEEN
 *  sites across components/, mfe/ and spa/, in BOTH member orders, under thirteen local names. */
export const FLEX_BETWEEN_START_STYLE = {
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'space-between',
} as const;

/** Flex column with horizontally centered children (main axis untouched) -- the empty-state / stacked-icon base. */
export const FLEX_COLUMN_ITEMS_CENTER_STYLE = {
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
} as const;

/** Flex column centered on BOTH axes -- the full-panel empty/loading-state base. */
export const FLEX_COLUMN_CENTER_STYLE = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexDirection: 'column',
} as const;

/** Inline-flex row centered on BOTH axes -- the icon-button base. */
export const INLINE_FLEX_CENTER_STYLE = {
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
} as const;

/** Truncation-safe flex CHILD (`flex: 1` + `minWidth: 0`) -- the inline-CSS sibling of `LAYOUT_CLASSES.flexFillTruncate`.
 * `minWidth: 0` is the standard escape from a flex item's min-content floor, and it is what lets the `textOverflow`
 * truncation on a descendant engage at all; the pair is ONE decision and neither half is useful alone here.
 *
 * HOMED HERE, not in `components/common/style-primitives.ts`: the value is a PURE LITERAL (it reads no `css-refs.*`
 * token, unlike that leaf's members) and the flex-idiom family lives here. This home is what makes the pair reachable
 * by a forbid-rule arm at all -- homed in a component leaf no preset matcher reads, it regrew to EIGHTEEN inline
 * spellings across apps/, components/, mfe/ and spa/ (2026-08-25).
 *
 * REGISTERED in `QUAL-STYLE-FLEX-IDIOM-INLINE-001`'s `PRESETS` record 2026-09-05, after the matcher was widened to
 * admit NUMERIC member values (its preset type, its run generator and its value index each took strings only, so the
 * registration would otherwise have armed nothing). The false-NEGATIVE cost recorded here as the blocking unknown was
 * MEASURED before the arm landed and answered by DESIGN rather than paid: replayed over the 1,755-file rendering tier,
 * folding `flex`/`minWidth`/`overflowY` into the flex family lost ZERO rows, but it also emptied that family's ARM 5
 * admission set (no preset spells all seven keys), which is a capability loss with no witness rather than none at all.
 * So the two FILL presets carry their own composition family instead -- see the TWO FAMILIES paragraph in this leaf's
 * header -- and the guard declines nothing it declined before. Day-one queue: SEVEN rows, all drained in that change. */
export const FLEX_FILL_TRUNCATE_STYLE = {
	flex: 1,
	minWidth: 0,
} as const;

/** The SCROLL REGION of a flex column (`flex: 1` + `overflowY: 'auto'`) -- the pane, drawer body, popover list and
 * form body that take the leftover height of a header/body/footer stack and scroll inside it. Neither half is useful
 * alone here: without `flex: 1` the box sizes to its content and never overflows, so `overflowY` never engages; with
 * only `flex: 1` a long list pushes the footer off the panel instead of scrolling under it. It was spelled longhand at
 * ELEVEN sites across `apps/`, `components/`, `mfe/` and `spa/`, in BOTH member orders, under nine local names.
 *
 * SAME `PRESETS`-REGISTRATION HISTORY AS {@link FLEX_FILL_TRUNCATE_STYLE} above, landed in the same change and for
 * the same reason: one member is a NUMBER, so the registration waited on the matcher widening. The measurement, and
 * the family split that answered the false-negative cost, is carried once on that preset rather than restated here. */
export const FLEX_FILL_SCROLL_Y_STYLE = {
	flex: 1,
	overflowY: 'auto',
} as const;
